import { useState, useEffect } from "react";
import UserImg from "@styles/all-images/images/Avatar.png";
import PlusGroup from "@styles/all-images/icons/plusgroup.svg";
import { CompanyGroup, ChatGroup, ChatUser } from "@/pages/LiveChat/types/chat";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import LiveChatGroupCreate from "./GroupChat/LiveChatCreatGroup";
import {
  UserTierType,
  UserTierTypeText,
  UserTierTypeTextShort,
} from "@/constants/enums/user-tier-type.enum";
import ChatSidebarSkeleton from "./components/skeletons/ChatSidebarSkeleton";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { ChatScreenType, ChatScreenState } from "./types/chat";
import { useLiveChatState } from "./Provider/LiveChatStateProvider";
import {
  CreateGroupResponse,
  GroupCreateResponse,
} from "./types/api-response-types";
import { LiveChatPermissions } from "@/constants/permission-modules/base-permissions";
import { usePermissionState } from "@/providers/PermissionProvider";
import ElipsedText from "@/components/common/elipsis/ElipsedText";
import { S3Image } from "@/utils/s3-helper";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";

interface LiveChatSidebarProps {
  onScreenChange: (screenState: ChatScreenState) => void;
}

const LiveChatSidebar = ({ onScreenChange }: LiveChatSidebarProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [expandedCompanies, setExpandedCompanies] = useState<{
    [key: string]: boolean;
  }>({});
  const [isGroupsExpanded, setIsGroupsExpanded] = useState(true);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isCompanyUsersLoading, setIsCompanyUsersLoading] = useState(false);
  const [isChatGroupsLoading, setIsChatGroupsLoading] = useState(false);
  const { isPermitted } = usePermissionState();

  const {
    currentScreen,
    companyGroups,
    chatGroups,
    setCompanyGroups,
    setChatGroups,
    setGeneralGroup,
    unreadCounts,
    resetUnreadCount,
    isIndividualChatCreationLoading,
    handleCreatingIndividualChatRoom,
  } = useLiveChatState();

  useEffect(() => {
    Promise.all([fetchCompanyUsers(), fetchChatGroups()]);
  }, []);

  const fetchCompanyUsers = async () => {
    try {
      setIsCompanyUsersLoading(true);
      const { authenticatedCall } = actionsHandler("getCompanyUsers");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
        }),
      );

      if (response.ok) {
        setCompanyGroups(response.data);

        // Initialize expanded state for all companies
        const initialExpandedState = response.data.reduce(
          (acc: any, group: CompanyGroup) => {
            group.companies.forEach((company) => {
              acc[company.company_id] = true;
            });
            return acc;
          },
          {},
        );
        setExpandedCompanies(initialExpandedState);
      }
    } catch (error) {
      console.error("Error fetching company users:", error);
    } finally {
      setIsCompanyUsersLoading(false);
    }
  };

  const fetchChatGroups = async (): Promise<ChatGroup[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsChatGroupsLoading(true);
        const { authenticatedCall } = actionsHandler("getChatGroups");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }),
        );

        const groups = response.data;
        // Separate general group from other groups
        const generalGroupData = groups.find(
          (group: ChatGroup) => group.name === "General",
        );
        const otherGroups = groups.filter(
          (group: ChatGroup) => group.name !== "General",
        );

        setGeneralGroup(generalGroupData || null);
        setChatGroups(otherGroups);
        resolve(otherGroups);
      } catch (error) {
        console.error("Error fetching chat groups:", error);
      } finally {
        setIsChatGroupsLoading(false);
        resolve([]);
      }
    });
  };

  const toggleCompany = (companyId: string) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: !prev[companyId],
    }));
  };

  const handleUserClick = (user: ChatUser, companyId: string) => {
    if (!user.room_id) {
      handleCreatingIndividualChatRoom(user).then(
        (response: CreateGroupResponse) => {
          onScreenChange({
            type: ChatScreenType.USER,
            data: {
              user: { ...user, room_id: response.room_id.toString() },
              companyId,
            },
          });
        },
      );
    } else {
      resetUnreadCount(user.room_id);
      onScreenChange({
        type: ChatScreenType.USER,
        data: { user, companyId },
      });
    }
  };

  const handleGroupClick = (group: ChatGroup) => {
    resetUnreadCount(group.room_id.toString());
    onScreenChange({
      type: ChatScreenType.GROUP,
      data: { group },
    });
  };

  const isSelected = ({
    type,
    user,
    group,
  }: {
    type: "user" | "group";
    user?: ChatUser;
    group?: ChatGroup;
  }) => {
    if (type === "user") {
      return user?.id === currentScreen?.data.user?.id;
    } else if (type === "group") {
      return group?.room_id === currentScreen?.data.group?.room_id;
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const activeChat = document.querySelector(".activeChat");
      if (activeChat) {
        activeChat.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [currentScreen]);

  const handleCreateGroupSuccess = async (group: GroupCreateResponse) => {
    const newGroups = await fetchChatGroups();
    const createdGroup = newGroups.find((g) => group.room_id === g.room_id);
    if (createdGroup) {
      handleGroupClick(createdGroup);
    }
  };

  return (
    <div className="tw-h-[calc(100vh-170px)] tw-pb-4 tw-overflow-y-auto">
      {!isCompanyUsersLoading ? (
        <>
          {companyGroups.map((group, groupIndex) =>
            group.companies.map((company, companyIndex) => (
              <div
                key={`${groupIndex}-${companyIndex}`}
                className="tw-w-80 tw-bg-[#ECF1F8] tw-rounded-xl tw-p-3 tw-mb-3"
              >
                <button
                  onClick={() => toggleCompany(company.company_id)}
                  className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 tw-rounded-lg tw-transition-colors"
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg
                      className={`tw-w-4 tw-h-4 tw-text-gray-600 tw-transition-transform tw-duration-200 ${
                        expandedCompanies[company.company_id]
                          ? "tw-rotate-0"
                          : "tw--rotate-90"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <span className="tw-font-normal tw-text-[#232323]">
                      {company.company_name}
                    </span>
                  </div>
                  <span className="tw-text-sm tw-text-gray-500">
                    {UserTierTypeTextShort[
                      company.user_type_id as UserTierType
                    ] || ""}
                  </span>
                </button>

                <div
                  className={`tw-transition-all tw-duration-200 tw-ease-in-out tw-overflow-auto ${
                    expandedCompanies[company.company_id]
                      ? "tw-max-h-96 tw-opacity-100"
                      : "tw-max-h-0 tw-opacity-0"
                  }`}
                >
                  {company.users.map((user, userIndex) => (
                    <button
                      key={userIndex}
                      onClick={() => handleUserClick(user, company.company_id)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        handleUserClick(user, company.company_id)
                      }
                      className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-3 hover:tw-bg-[#C2E7FE] tw-p-2 tw-rounded-xl tw-transition-colors tw-cursor-pointer tw-my-2 ${
                        isSelected({ type: "user", user })
                          ? "tw-bg-[#C2E7FE] activeChat"
                          : ""
                      }`}
                    >
                      <div className="tw-relative tw-flex tw-items-center tw-gap-2">
                        <div className="tw-relative">
                          <div className="tw-w-[25px] tw-h-[25px] tw-bg-[#ECF1F8] tw-rounded-full tw-overflow-hidden">
                            {user.profile_picture ? (
                              <S3Image
                                src={
                                  user.profile_picture?.replace(
                                    "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                                    "",
                                  ) || null
                                }
                                alt="Profile"
                                className="tw-w-10 tw-h-10 tw-rounded-full"
                              />
                            ) : (
                              <img
                                src={UserDropdown}
                                alt="Profile"
                                className="tw-rounded-full"
                              />
                            )}
                          </div>
                          <div
                            className={`tw-absolute tw-top-0 tw-right-0 tw-w-3 tw-h-3 ${
                              user.is_online
                                ? "tw-bg-[#12B76A]"
                                : "tw-bg-[#717680]"
                            } tw-rounded-full tw-border-2 tw-border-white`}
                          ></div>
                        </div>
                        <ElipsedText text={user.name} maxLength={15} />
                        {isIndividualChatCreationLoading[user.id] && (
                          <CustomLoader
                            loading={true}
                            isWrapper={true}
                            size="20"
                          />
                        )}
                        {unreadCounts[user.room_id?.toString() ?? ""] > 0 && (
                          <span className="tw-bg-[#146C94] tw-text-white tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs">
                            {unreadCounts[user.room_id?.toString() ?? ""]}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )),
          )}
        </>
      ) : (
        <ChatSidebarSkeleton isCompanyUsersLoading={isCompanyUsersLoading} />
      )}

      {/* Chat Groups Section */}
      {!isChatGroupsLoading ? (
        <div
          className={`tw-w-80 tw-bg-[#ECF1F8] tw-rounded-xl tw-p-3 tw-mb-3 ${isCompanyUsersLoading ? "tw-mt-3" : ""}`}
        >
          <div className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-p-2">
            <button
              onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
              className="tw-w-full tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-transition-colors"
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <svg
                  className={`tw-w-4 tw-h-4 tw-text-gray-600 tw-transition-transform tw-duration-200 ${
                    isGroupsExpanded ? "tw-rotate-0" : "tw--rotate-90"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span className="tw-font-normal tw-text-[#232323]">Groups</span>
              </div>
            </button>
            {isPermitted(LiveChatPermissions.CREATE_LIVE_CHAT) && (
              <button
                onClick={() => setIsCreateGroupModalOpen(true)}
                className="tw-flex tw-items-center tw-text-[#146C94] tw-gap-2 tw-text-sm"
              >
                <img
                  src={PlusGroup}
                  alt="Plus Group"
                  className="tw-w-5 tw-h-5 tw-min-h-5 tw-min-w-5"
                />
                Create
              </button>
            )}
          </div>

          <div
            className={`tw-transition-all tw-duration-200 tw-ease-in-out tw-overflow-auto ${
              isGroupsExpanded
                ? "tw-max-h-96 tw-opacity-100"
                : "tw-max-h-0 tw-opacity-0"
            }`}
          >
            {chatGroups.map((group) => (
              <button
                key={group.room_id}
                onClick={() => handleGroupClick(group)}
                onKeyDown={(e) => e.key === "Enter" && handleGroupClick(group)}
                className={`tw-w-full tw-flex tw-items-center tw-gap-3 hover:tw-bg-[#C2E7FE] tw-p-2 tw-rounded-xl tw-transition-colors tw-cursor-pointer tw-my-2 ${
                  isSelected({ type: "group", group })
                    ? "tw-bg-[#C2E7FE] activeChat"
                    : ""
                }`}
              >
                <div className="tw-relative">
                  <div className="tw-w-[25px] tw-h-[25px] tw-bg-[#ECF1F8] tw-border tw-border-[#CBD5E1] tw-rounded-full tw-overflow-hidden tw-text-center">
                    <h6 className="tw-text-[#146C94] tw-font-semibold tw-text-sm">
                      {group.name.charAt(0)}
                    </h6>
                  </div>
                </div>
                <span className="tw-font-normal tw-text-[#232323]">
                  {group.name}
                </span>
                {unreadCounts[group.room_id.toString()] > 0 && (
                  <span className="tw-bg-[#146C94] tw-text-white tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs">
                    {unreadCounts[group.room_id.toString()]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <ChatSidebarSkeleton isChatGroupsLoading={isChatGroupsLoading} />
      )}

      {isCreateGroupModalOpen && (
        <LiveChatGroupCreate
          onClose={() => setIsCreateGroupModalOpen(false)}
          onSuccess={handleCreateGroupSuccess}
        />
      )}
    </div>
  );
};

export default LiveChatSidebar;
